import { useAttachBriefDeliverable } from "@entities/brief/lib/hooks/useAttachDeliverable";
import useBulkActions from "@entities/brief/lib/hooks/useBulkActions";
import { useGetBriefDeliverables } from "@entities/brief/lib/hooks/useGetBriefDeliverables";
import { usePreCheckDeliverables } from "@entities/brief/lib/hooks/usePreCheckDeliverables";
import PackageForm from "@entities/brief/ui/Influencer/v2/PackageForm";
import CampaignDeliverablesWarningModal from "@entities/CampaignDeliverables/ui/CampaignDeliverablesWarningModal";
import { useBriefStore } from "@pages/layout/BriefLayout/lib/useLayoutStore";
import { ToastService } from "@shared/lib/services";
import { BriefNotificationActionType } from "@shared/lib/types/brief";
import {
  IBriefDeliverable,
  IBriefInfluencerSearchRequestV2,
  IBriefInfluencerV2,
} from "@shared/lib/types/briefInfluencerV2";
import { DeliverablePillSummary, Drawer } from "@shared/ui";
import { Button } from "@shared/ui/Button";
import type { ButtonProps } from "antd";
import { Divider, Dropdown, Menu, Popover } from "antd";
import { ArrowRight, Plus, TriangleAlert } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { CampaignDeliverables } from ".";
import styles from "./styles.module.scss";

export interface AddDeliverablesButtonProps {
  checkedList?: string[];
  selectAll?: boolean;
  uncheckList?: string[];
  totalCount?: number;
  searchPayload: Partial<IBriefInfluencerSearchRequestV2>;
  handleUpdateBriefInfluencer?: (
    data: { id: string; briefInfluencer: IBriefInfluencerV2 }[]
  ) => void;
  onSuccess?: () => void;
  buttonProps?: ButtonProps;
}

export function AddDeliverablesButton({
  checkedList,
  selectAll,
  uncheckList,
  totalCount,
  searchPayload,
  handleUpdateBriefInfluencer,
  onSuccess,
  buttonProps,
}: AddDeliverablesButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(0);
  const { briefId } = useBriefStore();
  const { deliverables: briefDeliverables } = useGetBriefDeliverables(briefId);

  const effectiveCount =
    selectAll && totalCount
      ? totalCount - (uncheckList?.length ?? 0)
      : checkedList?.length ?? 0;
  const { bulkActions, isBulkActions, isBulkActionsSuccess, bulkActionsError } =
    useBulkActions();
  const {
    attachBriefDeliverable,
    isAttached,
    attachBriefDeliverableError,
  } = useAttachBriefDeliverable(briefId);
  const [openWarningModal, setOpenWarningModal] = useState<{
    open: boolean;
    deliverable: IBriefDeliverable | null;
    preCheckDeliverables: {
      status: "failed" | "success" | "warning";
      briefInfluencerId: string;
      reason: string;
      briefInfluencer: IBriefInfluencerV2;
    }[];
    resetCost?: boolean;
  }>({
    open: false,
    deliverable: null,
    preCheckDeliverables: [],
  });

  type PreCheckItem = {
    status: "failed" | "success" | "warning";
    briefInfluencerId: string;
    reason: string;
    briefInfluencer: IBriefInfluencerV2;
  };

  const [costConfirmPopover, setCostConfirmPopover] = useState<{
    open: boolean;
    deliverable: IBriefDeliverable | null;
    validChecks?: PreCheckItem[];
  }>({
    open: false,
    deliverable: null,
    validChecks: undefined,
  });

  const [selectedDel, setSelectedDel] = useState<IBriefDeliverable | null>(
    null
  );

  const { preCheckDeliverables, isFetching, isSuccess, isError, refetch } =
    usePreCheckDeliverables({
      briefInfluencerIds: checkedList,
      isSelectAll: selectAll,
      notIncludeIds: uncheckList,
      filterParams: { ...searchPayload },
      deliverableV2Id: selectedDel?._id || "",
    });

  useEffect(() => {
    if (isError) {
      setSelectedDel(null);
    }
  }, [isError]);

  const handleExecuteAttach = (
    resetCost: boolean,
    targetDeliverable?: IBriefDeliverable | null,
    overrideValidChecks?: PreCheckItem[]
  ) => {
    const delToAttach =
      targetDeliverable || costConfirmPopover.deliverable || selectedDel;
    if (!delToAttach) return;

    const validChecks =
      overrideValidChecks !== undefined
        ? overrideValidChecks
        : costConfirmPopover.validChecks;

    setCostConfirmPopover({
      open: false,
      deliverable: null,
      validChecks: undefined,
    });
    setSelectedDel(null);

    const influencerIdsToAttach = validChecks
      ? validChecks.map((val) => val.briefInfluencerId)
      : checkedList || [];

    if (selectAll && !validChecks) {
      bulkActions({
        briefId,
        actionType: BriefNotificationActionType.attachDeliverables,
        filterParams: searchPayload,
        isSelectAll: selectAll || false,
        actionParams: {
          deliverableV2Id: delToAttach?._id || "",
          resetCost,
        },
        ids: checkedList || [],
        notIncludeIds: uncheckList || [],
      });
    } else {
      attachBriefDeliverable({
        briefInfluencerIds: influencerIdsToAttach,
        deliverableId: delToAttach?._id || "",
        resetCost,
      });
    }
  };

  useEffect(() => {
    if (isBulkActionsSuccess) {
      ToastService.success("Deliverable attached successfully.");
      onSuccess?.();
    } else if (bulkActionsError) {
      ToastService.error(bulkActionsError?.message);
    }
  }, [isBulkActionsSuccess, bulkActionsError]);

  useEffect(() => {
    if (isAttached) {
      ToastService.success("Deliverable attached successfully.");
      onSuccess?.();
    } else if (attachBriefDeliverableError) {
      ToastService.error(attachBriefDeliverableError?.message);
    }
  }, [isAttached, attachBriefDeliverableError]);

  useEffect(() => {
    if (selectedDel && isSuccess && !isFetching) {
      const affectedEntries = preCheckDeliverables?.filter(
        (item) => item?.status === "failed" || item?.status === "warning"
      );

      const hasWarning = preCheckDeliverables?.some(
        (item) => item?.status === "warning"
      );

      if (affectedEntries && affectedEntries.length > 0) {
        setOpenWarningModal({
          open: true,
          deliverable: selectedDel,
          preCheckDeliverables: preCheckDeliverables || [],
        });
      } else if (hasWarning) {
        setCostConfirmPopover({
          open: true,
          deliverable: selectedDel,
        });
      } else {
        handleExecuteAttach(false, selectedDel);
      }
      setIsOpen(false);
    }
  }, [isSuccess, isFetching, selectedDel, preCheckDeliverables]);

  const deliverableMenuItems = useMemo(() => {
    if (!briefDeliverables || briefDeliverables.length === 0) return [];
    return briefDeliverables
      .map((briefDeliverable, i) => {
        const deliverable = briefDeliverable.deliverableV2;
        if (!deliverable) return null;
        return {
          key: briefDeliverable?.deliverableV2?._id || `deliverable-${i}`,
          label: (
            <DeliverablePillSummary
              deliverable={briefDeliverable?.deliverableV2}
              showSpecialNotes
            />
          ),
          onClick: () => {
            if (selectedDel?._id === deliverable._id) {
              refetch();
            } else {
              setSelectedDel(deliverable);
            }
          },
          id: `Deliverable-${i}`
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null);
  }, [briefDeliverables]);

  const deliverableItems = useMemo(() => {
    if (deliverableMenuItems.length > 0) return deliverableMenuItems;
    return [
      {
        key: "no-deliverables",
        label: "No deliverables found",
        disabled: true,
      },
    ];
  }, [deliverableMenuItems]);

  const bottomMenuItems = useMemo(
    () => [
      {
        key: "add-deliverable",
        label: <span style={{color: "#635bff"}}>Add Deliverable</span>,
        icon: <Plus size={16} color="#635bff"/>,
        onClick: () => {
          setIsFormOpen(true);
          setIsOpen(false);
        },
      },
    ],
    []
  );

  const focusableItems = useMemo(() => {
    return [...deliverableMenuItems, ...bottomMenuItems];
  }, [deliverableMenuItems, bottomMenuItems]);

  useEffect(() => {
    if (isOpen) {
      setFocusedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setFocusedIndex((prev) =>
          focusableItems.length > 0 ? (prev + 1) % focusableItems.length : 0
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setFocusedIndex((prev) =>
          focusableItems.length > 0
            ? (prev - 1 + focusableItems.length) % focusableItems.length
            : 0
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusableItems[focusedIndex]) {
          focusableItems[focusedIndex].onClick();
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, focusableItems, focusedIndex]);

  useEffect(() => {
    document.getElementById(`Deliverable-${focusedIndex}`)?.scrollIntoView({
      block: "nearest"
    });
  }, [focusedIndex]);

  const renderTriggerButton = () => (
    <Button
      icon={<Plus size={16} />}
      loading={isFetching || isBulkActions}
      {...buttonProps}
      onClick={(e) => {
        setIsOpen((prev) => !prev);
        buttonProps?.onClick?.(e);
      }}
    >
      {buttonProps?.children !== undefined
        ? buttonProps.children
        : `Add Deliverables (${effectiveCount})`}
    </Button>
  );

  return (
    <>
      <Popover
        open={isFormOpen}
        onOpenChange={(open) => {
          if (!open) setIsFormOpen(false);
        }}
        rootClassName={styles.packageFormPopover}
        placement="right"
        destroyTooltipOnHide
        styles={{
          body: {
            width: 300,
          },
        }}
        trigger={["click"]}
        content={
          <PackageForm
            briefId={briefId}
            isEdit={false}
            isClone={false}
            onCancel={() => {
              setIsFormOpen(false);
            }}
            onSuccess={(deliverableData) => {
              setIsFormOpen(false);
              if (deliverableData) {
                setSelectedDel(deliverableData);
              }
            }}
            onBack={() => {
              setIsFormOpen(false);
              setIsOpen(true);
            }}
            showTitle={true}
          />
        }
      >
        <Popover
          open={costConfirmPopover.open}
          trigger={["click"]}
          onOpenChange={(open) => {
            if (!open) {
              setCostConfirmPopover({
                open: false,
                deliverable: null,
                validChecks: undefined,
              });
            }
          }}
          placement="right"
          destroyTooltipOnHide
          title={
            <div className={styles.costConfirmHeader}>
              <span className={styles.costConfirmTitle}>
                Confirm change
              </span>
              <button
                type="button"
                className={styles.costConfirmCancelBtn}
                onClick={() => {
                  setCostConfirmPopover({
                    open: false,
                    deliverable: null,
                    validChecks: undefined,
                  });
                  setSelectedDel(null);
                }}
              >
                Cancel
              </button>
            </div>
          }
          content={
            <div className={styles.costConfirmContent}>
              <div className={styles.costConfirmBody}>
                <TriangleAlert
                  size={16}
                  className={styles.costConfirmIcon}
                />
                <div>
                  <div className={styles.costConfirmHeading}>
                    Deliverables are changing
                  </div>
                  <div className={styles.costConfirmDescription}>
                    Do you want to reset the commercials? Status will move to{" "}
                    <span className={styles.costConfirmHighlight}>
                      Commercials Pending
                    </span>{" "}
                    and prices will need to be re-negotiated.
                  </div>
                </div>
              </div>
              <div className={styles.costConfirmFooter}>
                <Button
                  type="primary"
                  block
                  size="small"
                  className={styles.costConfirmPrimaryBtn}
                  onClick={() => handleExecuteAttach(true)}
                >
                  Yes, reset commercials
                </Button>
                <Button
                  block
                  size="small"
                  className={styles.costConfirmSecondaryBtn}
                  onClick={() => handleExecuteAttach(false)}
                >
                  No, keep commercials
                </Button>
              </div>
            </div>
          }
        >
          <Dropdown
            dropdownRender={() => (
              <div className={styles.dropdownContainer}>
                <div className={styles.dropdownHeader}>
                  <span className={styles.dropdownHeaderLabel}>
                    Select deliverables
                  </span>
                  <ArrowRight
                    size={14}
                    className={styles.headerArrowIcon}
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpen(false);
                      setIsDrawerOpen(true);
                    }}
                  />
                </div>
                <div className={styles.deliverablesMenuItems}>
                  <Menu
                    selectedKeys={
                      focusableItems[focusedIndex]
                        ? [focusableItems[focusedIndex].key]
                        : []
                    }
                    className={styles.menu}
                    items={deliverableItems}
                  />
                </div>
                <Divider className={styles.dividerResetMargin} />
                <Menu
                  selectedKeys={
                    focusableItems[focusedIndex]
                      ? [focusableItems[focusedIndex].key]
                      : []
                  }
                  className={`${styles.menu} ${styles.bottomMenuSection}`}
                  items={bottomMenuItems}
                />
                <div className={styles.dropdownFooter}>
                  <p className={styles.dropdownFooterText}>
                    ↑↓ navigate · Enter select · Esc close
                  </p>
                </div>
              </div>
            )}
            trigger={["click"]}
            overlayStyle={{
              maxWidth: 300,
            }}
            open={isOpen}
            onOpenChange={(open) => {
              if (!open) setIsOpen(false);
            }}
            placement="bottomLeft"
            destroyPopupOnHide
          >
            {renderTriggerButton()}
          </Dropdown>
        </Popover>
      </Popover>

      <CampaignDeliverablesWarningModal
        open={openWarningModal.open}
        preCheckDeliverables={openWarningModal.preCheckDeliverables}
        handleUpdateInfluencer={handleUpdateBriefInfluencer}
        payload={{
          checkedList,
          uncheckList,
          selectAll,
          searchPayload,
          deliverableId: openWarningModal.deliverable?._id || "",
          resetCost: openWarningModal.resetCost
        }}
        onCancel={() => {
          setOpenWarningModal({
            open: false,
            deliverable: null,
            preCheckDeliverables: [],
          });
          setSelectedDel(null);
        }}
        onBack={() => {
          setOpenWarningModal({
            open: false,
            deliverable: null,
            preCheckDeliverables: [],
          });
          // setSelectedDel(null);
          setIsOpen(true);
        }}
        onConfirm={(validChecks: PreCheckItem[]) => {
          const del = openWarningModal.deliverable || selectedDel;
          setOpenWarningModal({
            open: false,
            deliverable: null,
            preCheckDeliverables: [],
          });
          const hasWarning = validChecks?.some(
            (val) => val.status === "warning"
          );
          if (hasWarning) {
            setCostConfirmPopover({
              open: true,
              deliverable: del,
              validChecks,
            });
          } else {
            handleExecuteAttach(false, del, validChecks);
          }
        }}
        onSuccess={() => {
          setOpenWarningModal({
            open: false,
            deliverable: null,
            preCheckDeliverables: [],
          });
          setSelectedDel(null);
          onSuccess?.();
        }}
      />

      <Drawer
        open={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        title="Campaign Deliverables"
        width={800}
        footer={null}
        destroyOnClose
        zIndex={1100}
        styles={{
          body: {
            padding: 16,
          },
        }}
      >
        <CampaignDeliverables showTitle={false} />
      </Drawer>
    </>
  );
}
